import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Upload, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { DIVISIONS, PACKAGING_OPTIONS } from "@/constants/site";
const PRODUCT_OPTIONS = [
  "MR311-R Water Removable Red Penetrant",
  "MR62 Red Penetrant",
  "MR70I White Developer",
  "MR85 Remover",
  "SC20 Solvent Cleaner",
  "SP10 Solvent Removable Penetrant",
  "SD30 Solvent Developer",
  "ZINCKED 50 Basic Bright Grade",
  "ZINCKED 150 Bright Grade",
  "ZINCKED 550 Bright Grade Pro",
  "ZINCKED 650 Ultra Bright Grade Pro",
  "ZINCKED 1050 Zinc Primer",
  "ZINCKED 1225 Zinc Coating",
  "ZINCKED 1525 Zinc Primer",
  "Bearing / Bearing Number",
  "BESSEY Clamping Tools",
  "Other Product"
];
export default function QuotationDialog({ open, onOpenChange, prefill = {} }) {
  const [form, setForm] = useState({
    full_name: "", company_name: "", phone: "", email: "",
  city: "", state: "Telangana", divisions: [], product_name: "", other_product: "",
quantity: "", packaging: "", application: "", message: "", file_url: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
const [selectedProducts, setSelectedProducts] = useState([]);
const [selectedDivisions, setSelectedDivisions] = useState([]);
const toggleProduct = (product) => {
  setSelectedProducts((current) => {
    const next = current.includes(product)
      ? current.filter((p) => p !== product)
      : [...current, product];

    setForm((f) => ({
      ...f,
      product_name: next.join(", "),
    }));

    return next;
  });
};

const toggleDivision = (division) => {
  setSelectedDivisions((current) => {
    const next = current.includes(division)
      ? current.filter((d) => d !== division)
      : [...current, division];

    setForm((f) => ({
      ...f,
      divisions: next,
    }));

    return next;
  });
};
 
  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, ...prefill }));
      setSuccess(null);
    }
  }, [open, JSON.stringify(prefill)]);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be under 15 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, file_url: data.url }));
      toast.success("File uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email) { toast.error("Please fill required fields"); return; }
    setSubmitting(true);
    try {
     const payload = {
  ...form,
  division: form.divisions.join(", "),
};

const { data } = await api.post("/enquiries", payload);
      setSuccess(data);
      toast.success(`Enquiry ${data.reference} received`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="quotation-dialog">
        <DialogHeader>
          <div className="text-[11px] font-mono-tech tracking-[0.18em] uppercase text-slate-500">Request a Quotation</div>
          <DialogTitle className="font-display text-2xl">Tell us what you need</DialogTitle>
          <DialogDescription className="text-slate-600">
  Select the relevant product divisions and products below. If your required product is not listed, select "Other Product" and enter the details. Our team will review your requirement and provide the appropriate product information and quotation.
</DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center" data-testid="quote-success">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
            <h3 className="font-display text-xl font-bold">Enquiry Received</h3>
            <p className="text-slate-600 mt-3 leading-relaxed">
              Thank you for contacting SSK Enterprises. Your enquiry has been received successfully. Our team will get back to you as soon as possible.
            </p>
            <div className="mt-5 inline-block bg-slate-100 border border-slate-200 px-4 py-2">
              <div className="overline">Reference</div>
              <div className="font-mono-tech font-semibold text-[#0F2747]" data-testid="quote-reference">{success.reference}</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="quotation-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full Name *"><Input required value={form.full_name} onChange={change("full_name")} data-testid="q-name" /></Field>
              <Field label="Company Name"><Input value={form.company_name} onChange={change("company_name")} data-testid="q-company" /></Field>
              <Field label="Phone *"><Input required value={form.phone} onChange={change("phone")} data-testid="q-phone" /></Field>
              <Field label="Email *"><Input type="email" required value={form.email} onChange={change("email")} data-testid="q-email" /></Field>
              <Field label="City"><Input value={form.city} onChange={change("city")} data-testid="q-city" /></Field>
              <Field label="State">
                <Select value={form.state} onValueChange={change("state")}>
                  <SelectTrigger data-testid="q-state"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Telangana">Telangana</SelectItem>
                    <SelectItem value="Andhra Pradesh">Andhra Pradesh</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
             <Field label="Product Divisions Required">
  <div className="rounded-md border border-slate-300 bg-white p-3 max-h-52 overflow-y-auto">
    {Object.values(DIVISIONS).map((d) => (
      <label
        key={d.key}
        className="flex items-center gap-3 py-2 cursor-pointer"
      >
        <input
          type="checkbox"
          checked={selectedDivisions.includes(d.key)}
          onChange={() => toggleDivision(d.key)}
        />
        <span className="text-sm">{d.label}</span>
      </label>
    ))}
  </div>

  {selectedDivisions.length > 0 && (
    <p className="mt-2 text-xs text-slate-500">
      {selectedDivisions.length} division(s) selected
    </p>
  )}
</Field>
              <Field label="Products Required">
  <div className="rounded-md border border-slate-300 bg-white p-3 max-h-64 overflow-y-auto">
    {PRODUCT_OPTIONS.map((product) => (
      <label
        key={product}
        className="flex items-center gap-3 py-2 cursor-pointer"
      >
        <input
          type="checkbox"
          checked={selectedProducts.includes(product)}
          onChange={() => toggleProduct(product)}
        />
        <span className="text-sm">{product}</span>
      </label>
    ))}
  </div>

  {selectedProducts.length > 0 && (
    <p className="mt-2 text-xs text-slate-500">
      {selectedProducts.length} product(s) selected
    </p>
  )}
</Field>
              {selectedProducts.includes("Other Product") && (
  <Field label="Other Product Details">
    <Input
      value={form.other_product}
      onChange={change("other_product")}
      placeholder="Enter product name or requirement"
      data-testid="q-other-product"
    />
  </Field>
)}
              <Field label="Required Quantity"><Input value={form.quantity} onChange={change("quantity")} data-testid="q-quantity" /></Field>
              <Field label="Packaging (NDT / Zinc) or Bearing details">
                <Select value={form.packaging} onValueChange={change("packaging")}>
                  <SelectTrigger data-testid="q-packaging"><SelectValue placeholder="Select packaging" /></SelectTrigger>
                  <SelectContent>
                    {PACKAGING_OPTIONS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Application / Industry"><Input value={form.application} onChange={change("application")} data-testid="q-application" /></Field>
            <Field label="Message"><Textarea rows={3} value={form.message} onChange={change("message")} data-testid="q-message" /></Field>
            <Field label="Attach bearing photo / drawing / spec (optional)">
              <label className="flex items-center gap-3 cursor-pointer border border-dashed border-slate-300 hover:border-[#0F2747] px-4 py-3 bg-slate-50">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span className="text-sm text-slate-600">
                  {form.file_url ? "File attached — replace" : "Click to upload (max 15 MB)"}
                </span>
                <input type="file" className="hidden" onChange={uploadFile} data-testid="q-file" />
              </label>
            </Field>
            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="q-submit">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit Quotation Request
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
